import { ThemeOptions } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import linkBase from '../common/linkBase';
import typography from '../common/typography';
import { createBreakpoints } from '@mui/system';
import solBreakpoints from '../common/breakpoints';
import ColourPalette from '../common/colorPalette';
import { alertClasses } from '@mui/material/Alert';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    fluid: true;
    fixed: true;
    unstyled: true;
  }

  interface ButtonPropsSizeOverrides {
    extraSmall: true;
    small: true;
    medium: true;
    large: true;
  }
  interface ButtonPropsColorOverrides {
    neutral: true;
    danger: true;
    success: true;
    warning: true;
    subtle: true;
    ghost: true;
    primary: true;
    link: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsSizeOverrides {
    extraSmall: true;
    small: true;
    medium: true;
    large: true;
  }
  interface IconButtonPropsColorOverrides {
    neutral: true;
    danger: true;
    success: true;
    warning: true;
    subtle: true;
    ghost: true;
    ghostGray: true;
    primary: true;
    link: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    bright: true;
    medium: true;
    dark: true;
  }
}

const breakpoints = createBreakpoints(solBreakpoints);

const getEqMainThemeOptions: () => ThemeOptions = () => ({
  baseMediaUrl: 'https://eqsolintusapp.blob.core.windows.net/web-resources',
  typography,
  palette: {
    common: {
      black: '#0C192F',
      white: '#ffffff',
    },
    primary: {
      main: '#0C41C9',
      light: '#5B7FFF',
      dark: '#1C1F4C',
      '10': '#1C1F4C',
      '20': '#19316F',
      '30': '#002B9B',
      '40': '#0C41C9',
      '50': '#124EDE',
      '60': '#2E64F0',
      '70': '#5B7FFF',
      '80': '#8DA6FF',
      '90': '#B2C8FF',
      '95': '#D8E3FF',
      '99': '#ECF1FF',
      '100': '#F5F8FF',
    },
    secondary: {
      main: '#0C192F',
    },
    tertiary: {
      main: '#3B485D',
    },
    info: {
      main: '#7E95BF',
    },
    warning: {
      main: '#F4AE3D',
      light: '#FCF0BA',
      dark: '#5E0000',
      '10': '#5D0000',
      '20': '#751C00',
      '30': '#C85500',
      '40': '#F19035',
      '50': '#F4AE3D',
      '60': '#F7C444',
      '70': '#FBE38E',
      '80': '#FCF0BA',
      '90': '#FFF6CD',
      '95': '#FEFAE8',
      '100': '#FEFCF4',
    },
    insightful: {
      main: '#5932C6',
      light: '#9B77FF',
      dark: '#221D63',
      '10': '#221D63',
      '20': '#352E87',
      '30': '#423A9C',
      '40': '#5932C6',
      '50': '#6C40E8',
      '60': '#8459FA',
      '70': '#9B77FF',
      '80': '#AE9AFF',
      '90': '#C6BAF4',
      '95': '#E3DCFF',
      '99': '#F4F1FE',
    },
    royal: {
      main: '#167286',
      light: '#50C0DA',
      dark: '#0A3848',
      '10': '#0A3848',
      '20': '#07475D',
      '30': '#035E72',
      '40': '#167286',
      '50': '#00819E',
      '60': '#1CA4C2',
      '70': '#50C0DA',
      '80': '#83DBEF',
      '90': '#B3E9F5',
      '95': '#DAF8FF',
      '99': '#EEFCFF',
    },
    danger: {
      main: '#DE0A14',
      light: '#F4676E',
      dark: '#AD0E11',
      '10': '#470003',
      '20': '#860003',
      '30': '#AD0E11',
      '40': '#DE0A14',
      '50': '#F4676E',
      '60': '#FFA4AC',
      '70': '#FFC8CD',
      '80': '#FFD8DC',
      '90': '#FAE2E5',
      '95': '#FFEFF1',
      '100': '#FFF9F9',
    },
    muave: {
      main: '#932956',
      light: '#DF5891',
      dark: '#462232',
      '10': '#462232',
      '20': '#632640',
      '30': '#7A2A4C',
      '40': '#932956',
      '50': '#A52259',
      '60': '#C2326F',
      '70': '#DF5891',
      '80': '#F289B5',
      '90': '#FAC4DA',
      '95': '#FFE5F0',
      '99': '#FFF5F9',
    },
    neutral: {
      main: '#3B485D',
      light: '#FFFFFF',
      dark: '#0C192F',
      '10': '#0C192F',
      '20': '#2C384B',
      '30': '#3B485D',
      '40': '#536077',
      '50': '#616C82',
      '55': '#6D778A',
      '60': '#A1A8B4',
      '65': '#B6BBC6',
      '70': '#C5C9D3',
      '75': '#D0D4DC',
      '80': '#DCDFE4',
      '85': '#E9EBEE',
      '90': '#F3F4F6',
      '95': '#F9F9FB',
      '100': '#FFFFFF',
    },
    success: {
      main: '#36B37E',
      light: '#BBF2D3',
      dark: '#112A1F',
      '10': '#112A1F',
      '20': '#005237',
      '30': '#006644',
      '40': '#00875A',
      '50': '#36B37E',
      '60': '#7CD5A7',
      '70': '#98EEC3',
      '80': '#BBF2D3',
      '90': '#D4F9E4',
      '95': '#E8FBF0',
      '100': '#F3FFF8',
    },
  },
  // prettier-ignore
  spacing: [0,
    4, 8, 16, 24, // small
    32, 40, 56, 72, // medium
    112, 128, 272], // large
  baselines: ['20px', '28px'],
  breakpoints: solBreakpoints,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // iOS fix for zoom on iOS
          'select:focus, textarea:focus, input[type="text"]:focus, input[type="number"]:focus, input[type="email"]:focus, input[type="search"]:focus, input[type="tel"]:focus':
            {
              [breakpoints.down('sm')]: {
                height: '20px',
                fontSize: '16px !important',
              },
            },
        },
        a: {
          ...linkBase,
        },
        form: {
          '& .MuiLoadingButton-root.Mui-disabled': {
            backgroundColor: '#F0F1F5', // COLORS
          },
          '& .MuiLoadingButton-loadingIndicator': {
            backgroundColor: '#F0F1F5', // COLORS
          },
        },
        strong: {
          fontWeight: 600,
        },
        b: {
          fontWeight: 600,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          columnGap: '8px',
          padding: '16px',
          fontSize: typography?.body3?.fontSize,
          fontWeight: typography?.body3?.fontWeight,
          lineHeight: typography?.body3?.lineHeight,
          borderLeftWidth: 4,
          borderLeftStyle: 'solid',
          '.MuiSvgIcon-root': {
            fontSize: typography?.body2?.fontSize,
          },
          [`&.${alertClasses.standard}.${alertClasses.colorSuccess}`]: {
            color: ColourPalette.palette.success['10'],
            backgroundColor: ColourPalette.palette.success['95'],
            borderLeftColor: ColourPalette.palette.success['40'],
            '.MuiAlert-icon, .MuiSvgIcon-root': {
              color: ColourPalette.palette.success['10'],
            },
          },
          [`&.${alertClasses.standard}.${alertClasses.colorWarning}`]: {
            color: ColourPalette.palette.warning['10'],
            backgroundColor: ColourPalette.palette.warning['95'],
            borderLeftColor: ColourPalette.palette.warning['40'],
            '.MuiAlert-icon, .MuiSvgIcon-root': {
              color: ColourPalette.palette.warning['10'],
            },
          },
          [`&.${alertClasses.standard}.${alertClasses.colorError}`]: {
            color: ColourPalette.palette.danger['20'],
            backgroundColor: ColourPalette.palette.danger['95'],
            borderLeftColor: ColourPalette.palette.danger['30'],
            '.MuiAlert-icon, .MuiSvgIcon-root': {
              color: ColourPalette.palette.danger['20'],
            },
          },
          [`&.${alertClasses.standard}.${alertClasses.colorInfo}`]: {
            color: ColourPalette.palette.primary['10'],
            backgroundColor: ColourPalette.palette.primary['99'],
            borderLeftColor: ColourPalette.palette.primary['30'],
            '.MuiAlert-icon, .MuiSvgIcon-root': {
              color: ColourPalette.palette.primary['10'],
            },
          },
        },
        icon: {
          alignItems: 'center',
          height: 20,
          minWidth: 20,
          margin: 0,
          padding: 0,
          opacity: 1,
          color: ColourPalette.palette.primary['10'],
        },
        message: {
          padding: 0,
        },
        action: {
          margin: 0,
          padding: 0,
          '.MuiButtonBase-root': {
            marginTop: '-6px',
          },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <KeyboardArrowDownIcon />,
      },
      styleOverrides: {
        noOptions: {
          fontSize: typography?.body3?.fontSize,
        },
        root: {
          '& .MuiInputLabel-root': {
            fontSize: typography?.body3?.fontSize,
          },
          '& .MuiSvgIcon-root': {
            color: ColourPalette.palette.primary['40'],
          },
          '&.Mui-expanded .MuiOutlinedInput-root fieldset': {
            marginTop: '1px', // FIX for: the top border is cut off by 1px
            borderWidth: '2px',
            borderColor: ColourPalette.palette.primary['40'],
          },
          '& .MuiAutocomplete-popupIndicator': {
            height: '24px',
            minWidth: '24px',
            padding: 0,
          },
          '& .MuiAutocomplete-clearIndicator': {
            height: '24px',
            minWidth: '24px',
            margin: 0,
            marginRight: '4px',
            padding: 0,
            '&:hover': {
              backgroundColor: ColourPalette.palette.neutral['95'],
            },
            '& .MuiSvgIcon-root': {
              height: 14,
              minWidth: 14,
            },
          },
        },
        paper: {
          marginTop: '4px',
          borderRadius: '8px',
        },
        listbox: {
          padding: '8px',
          '& .MuiAutocomplete-option': {
            padding: '8px',
            lineHeight: typography?.body3?.lineHeight,
            fontSize: typography?.body3?.fontSize,
            color: ColourPalette.palette.neutral['30'],
            borderRadius: '4px',
            '&:hover': {
              color: ColourPalette.palette.neutral['10'],
              backgroundColor: ColourPalette.palette.neutral['90'],
            },
            '&.Mui-focusVisible': {
              color: ColourPalette.palette.neutral['10'],
              backgroundColor: ColourPalette.palette.neutral['95'],
              '&:hover': {
                backgroundColor: ColourPalette.palette.neutral['90'],
              },
            },
            '&[aria-selected="true"]': {
              backgroundColor: ColourPalette.palette.neutral['95'],
              '&:hover': {
                backgroundColor: ColourPalette.palette.neutral['90'],
              },
            },
            '&[aria-selected="true"].Mui-focused': {
              backgroundColor: ColourPalette.palette.neutral['95'],
              '&:hover': {
                backgroundColor: ColourPalette.palette.neutral['90'],
              },
            },
          },
        },
        inputRoot: {
          fontSize: typography?.body3Medium?.fontSize,
          fontWeight: typography?.body3Medium?.fontWeight,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
      },
      variants: [
        {
          props: { variant: 'fixed' },
          style: {
            width: '100%',
          },
        },
        {
          props: { size: 'extraSmall' },
          style: {
            height: '24px',
            padding: '4px 8px',
            fontSize: typography.body5Bold?.fontSize,
            fontWeight: typography.body5Bold?.fontWeight,
            lineHeight: typography.body5Bold?.lineHeight,
            '.MuiSvgIcon-root': {
              fontSize: typography?.body2?.fontSize,
            },
          },
        },
        {
          props: { size: 'small' },
          style: {
            height: '32px',
            padding: '7px 14px',
            fontSize: typography.body4Bold?.fontSize,
            fontWeight: typography.body4Bold?.fontWeight,
            lineHeight: typography.body4Bold?.lineHeight,
            '.MuiSvgIcon-root': {
              height: 18,
              minWidth: 18,
              fontSize: '18px',
            },
          },
        },
        {
          props: { size: 'medium' },
          style: {
            height: '40px',
            padding: '10px 20px',
            fontSize: typography.body3Bold?.fontSize,
            fontWeight: typography.body3Bold?.fontWeight,
            lineHeight: typography.body3Bold?.lineHeight,
            '.MuiSvgIcon-root': {
              height: 20,
              minWidth: 20,
              fontSize: '20px',
            },
          },
        },
        {
          props: { size: 'large' },
          style: {
            height: '48px',
            padding: '12px 24px',
            fontSize: typography.body2Bold?.fontSize,
            fontWeight: typography.body2Bold?.fontWeight,
            lineHeight: typography.body2Bold?.lineHeight,
            '.MuiSvgIcon-root': {
              height: 24,
              minWidth: 24,
              fontSize: '24px',
            },
          },
        },
        {
          props: { color: 'link' },
          style: {
            height: 'auto',
            padding: 0,
            fontWeight: 500,
            color: ColourPalette.palette.primary.main,
            backgroundColor: 'none',
            '&:hover': {
              color: ColourPalette.palette.primary['50'],
              textDecoration: 'underline',
              textUnderlineOffset: '4px',
              backgroundColor: 'none',
            },
            '&:focus-visible': {
              color: ColourPalette.palette.primary['70'],
              backgroundColor: 'none',
              outline: '0 none',
            },
            '&:active': {
              backgroundColor: 'none',
            },
            '&.Mui-disabled': {
              color: ColourPalette.palette.neutral['60'],
              backgroundColor: 'none',
            },
          },
        },
        {
          props: { color: 'primary' },
          style: {
            color: '#fff',
            backgroundColor: ColourPalette.palette.primary.main,
            '&:hover': {
              backgroundColor: ColourPalette.palette.primary['50'],
            },
            '&:focus-visible': {
              backgroundColor: ColourPalette.palette.primary.main,
            },
            '&:active': {
              backgroundColor: ColourPalette.palette.primary['30'],
            },
            '.MuiSvgIcon-root': {
              color: '#fff',
            },
          },
        },
        {
          props: { color: 'subtle' },
          style: {
            color: ColourPalette.palette.primary.main,
            backgroundColor: ColourPalette.palette.primary['95'],
            '&:hover': {
              backgroundColor: ColourPalette.palette.primary['99'],
            },
            '&:focus-visible': {
              backgroundColor: ColourPalette.palette.primary['95'],
            },
            '&:active': {
              backgroundColor: ColourPalette.palette.primary['90'],
            },
          },
        },
        {
          props: { color: 'neutral' },
          style: {
            color: ColourPalette.palette.primary.main,
            backgroundColor: ColourPalette.palette.neutral['85'],
            '&:hover': {
              backgroundColor: ColourPalette.palette.neutral['90'],
            },
            '&:focus-visible': {
              backgroundColor: ColourPalette.palette.neutral['80'],
            },
            '&:active': {
              backgroundColor: ColourPalette.palette.neutral['80'],
            },
          },
        },
        {
          props: { color: 'ghost' },
          style: {
            color: ColourPalette.palette.primary.main,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: ColourPalette.palette.neutral['95'],
            },
            '&:focus-visible': {
              backgroundColor: 'transparent',
            },
            '&:active': {
              backgroundColor: ColourPalette.palette.neutral['85'],
            },
            '&.Mui-disabled': {
              color: ColourPalette.palette.neutral['55'],
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { color: 'danger' },
          style: {
            color: '#fff',
            backgroundColor: ColourPalette.palette.danger['30'],
            '&:hover': {
              backgroundColor: ColourPalette.palette.danger['40'],
            },
            '&:focus-visible': {
              backgroundColor: ColourPalette.palette.danger['30'],
            },
            '&:active': {
              backgroundColor: ColourPalette.palette.neutral['20'],
            },
          },
        },
        {
          props: { color: 'danger', variant: 'outlined' }, // Login/Register buttons
          style: {
            color: '#fff',
            backgroundColor: '#fff',
            borderColor: ColourPalette.palette.danger['30'],
            '&:hover': {
              color: '#fff',
              backgroundColor: ColourPalette.palette.danger['40'],
            },
            '&:focus-visible': {
              color: '#fff',
              backgroundColor: ColourPalette.palette.danger['30'],
            },
            '&:active': {
              color: '#fff',
              backgroundColor: ColourPalette.palette.neutral['20'],
            },
          },
        },
        {
          props: { color: 'warning' },
          style: {
            color: ColourPalette.palette.danger['30'],
            backgroundColor: ColourPalette.palette.warning['60'],
            '&:hover': {
              backgroundColor: ColourPalette.palette.warning['70'],
            },
            '&:focus': {
              backgroundColor: ColourPalette.palette.warning['60'],
            },
            '&:active': {
              backgroundColor: ColourPalette.palette.warning['40'],
            },
          },
        },
        {
          props: { variant: 'unstyled' }, // Dark text
          style: {
            padding: 0,
            color: ColourPalette.palette.neutral['10'],
            backgroundColor: 'none',
            '&:hover': {
              backgroundColor: 'none',
            },
            '&:focus': {
              backgroundColor: 'none',
            },
            '&:active': {
              backgroundColor: 'none',
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          '&:focus': {
            outline: '0 none',
          },
          '&:focus-visible': {
            outlineWidth: '2px',
            outlineStyle: 'solid',
            outlineColor: ColourPalette.palette.primary['90'],
            outlineOffset: '2px',
          },
          '&.Mui-disabled': {
            color: ColourPalette.palette.neutral['55'],
            backgroundColor: ColourPalette.palette.neutral['90'],
            '.MuiSvgIcon-root': {
              color: ColourPalette.palette.neutral['55'],
            },
          },
          '& .MuiButton-startIcon': {
            marginLeft: '-2px',
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      variants: [
        {
          props: { size: 'extraSmall' },
          style: {
            height: '24px',
            minWidth: '24px',
            padding: '4px',
            fontSize: typography.body5Bold?.fontSize,
            fontWeight: typography.body5Bold?.fontWeight,
            lineHeight: typography.body5Bold?.lineHeight,
            '.MuiSvgIcon-root': {
              fontSize: '12px',
            },
          },
        },
        {
          props: { size: 'small' },
          style: {
            height: '32px',
            minWidth: '32px',
            padding: '6px',
            fontSize: typography.body4Bold?.fontSize,
            fontWeight: typography.body4Bold?.fontWeight,
            lineHeight: typography.body4Bold?.lineHeight,
            '.MuiSvgIcon-root': {
              height: 18,
              minWidth: 18,
              fontSize: '18px',
            },
          },
        },
        {
          props: { size: 'medium' },
          style: {
            height: '40px',
            minWidth: '40px',
            padding: '8px',
            fontSize: typography.body3Bold?.fontSize,
            fontWeight: typography.body3Bold?.fontWeight,
            lineHeight: typography.body3Bold?.lineHeight,
            '.MuiSvgIcon-root': {
              height: 20,
              minWidth: 20,
              fontSize: '20px',
            },
          },
        },
        {
          props: { size: 'large' },
          style: {
            height: '48px',
            minWidth: '48px',
            padding: '10px',
            fontSize: typography.body2Bold?.fontSize,
            fontWeight: typography.body2Bold?.fontWeight,
            lineHeight: typography.body2Bold?.lineHeight,
            '.MuiSvgIcon-root': {
              height: 24,
              minWidth: 24,
              fontSize: '24px',
            },
          },
        },
        {
          props: { color: 'link' },
          style: {
            height: 'auto',
            padding: 0,
            color: ColourPalette.palette.primary.main,
            backgroundColor: 'none',
            '&:hover': {
              color: ColourPalette.palette.primary['50'],
              textDecoration: 'underline',
              textUnderlineOffset: '4px',
              backgroundColor: 'none',
            },
            '&:focus-visible': {
              color: ColourPalette.palette.primary['70'],
              backgroundColor: 'none',
              outline: '0 none',
            },
            '&:active': {
              backgroundColor: 'none',
            },
            '&.Mui-disabled': {
              color: ColourPalette.palette.neutral['60'],
              backgroundColor: 'none',
            },
          },
        },
        {
          props: { color: 'primary' },
          style: {
            color: '#fff',
            backgroundColor: ColourPalette.palette.primary.main,
            '&:hover': {
              backgroundColor: ColourPalette.palette.primary['50'],
            },
            '&:focus-visible': {
              backgroundColor: ColourPalette.palette.primary.main,
            },
            '&:active': {
              backgroundColor: ColourPalette.palette.primary['30'],
            },
          },
        },
        {
          props: { color: 'subtle' },
          style: {
            color: ColourPalette.palette.primary.main,
            backgroundColor: ColourPalette.palette.primary['95'],
            '&:hover': {
              backgroundColor: ColourPalette.palette.primary['99'],
            },
            '&:focus-visible': {
              backgroundColor: ColourPalette.palette.primary['95'],
            },
            '&:active': {
              backgroundColor: ColourPalette.palette.primary['90'],
            },
          },
        },
        {
          props: { color: 'neutral' },
          style: {
            color: ColourPalette.palette.primary.main,
            backgroundColor: ColourPalette.palette.neutral['85'],
            '&:hover': {
              backgroundColor: ColourPalette.palette.neutral['90'],
            },
            '&:focus-visible': {
              backgroundColor: ColourPalette.palette.neutral['80'],
            },
            '&:active': {
              backgroundColor: ColourPalette.palette.neutral['80'],
            },
          },
        },
        {
          props: { color: 'ghost' },
          style: {
            color: ColourPalette.palette.primary.main,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: ColourPalette.palette.neutral['95'],
            },
            '&:focus-visible': {
              backgroundColor: 'transparent',
            },
            '&:active': {
              backgroundColor: ColourPalette.palette.neutral['85'],
            },
            '&.Mui-disabled': {
              color: ColourPalette.palette.neutral['55'],
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { color: 'ghostGray' },
          style: {
            color: ColourPalette.palette.neutral['30'],
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: ColourPalette.palette.neutral['95'],
            },
            '&:focus-visible': {
              backgroundColor: 'transparent',
            },
            '&:active': {
              color: ColourPalette.palette.neutral['20'],
              backgroundColor: ColourPalette.palette.neutral['85'],
            },
            '&.Mui-disabled': {
              color: ColourPalette.palette.neutral['55'],
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { color: 'danger' },
          style: {
            color: '#fff',
            backgroundColor: ColourPalette.palette.danger['30'],
            '&:hover': {
              backgroundColor: ColourPalette.palette.danger['40'],
            },
            '&:focus-visible': {
              backgroundColor: ColourPalette.palette.danger['30'],
            },
            '&:active': {
              backgroundColor: ColourPalette.palette.neutral['20'],
            },
          },
        },
        {
          props: { color: 'warning' },
          style: {
            color: ColourPalette.palette.danger['30'],
            backgroundColor: ColourPalette.palette.warning['60'],
            '&:hover': {
              backgroundColor: ColourPalette.palette.warning['70'],
            },
            '&:focus': {
              backgroundColor: ColourPalette.palette.warning['60'],
            },
            '&:active': {
              backgroundColor: ColourPalette.palette.warning['40'],
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          padding: '10px',
          borderRadius: 4,
          textTransform: 'none',
          '&:focus': {
            outline: '0 none',
          },
          '&:focus-visible': {
            outlineWidth: '2px',
            outlineStyle: 'solid',
            outlineColor: ColourPalette.palette.primary['90'],
            outlineOffset: '2px',
          },
          '&.Mui-disabled': {
            color: ColourPalette.palette.neutral['55'],
            backgroundColor: ColourPalette.palette.neutral['90'],
            '& .MuiSvgIcon-root': {
              color: ColourPalette.palette.neutral['55'],
            },
          },
          '&.MuiAutocomplete-popupIndicator.Mui-disabled': {
            background: 'none',
          },
          '& .MuiButton-startIcon': {
            marginLeft: '-2px',
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          height: '25px',
          width: '25px',
          padding: 0,
          '& .MuiSvgIcon-root': {
            color: ColourPalette.palette.neutral['75'],
          },
          '&.Mui-checked .MuiSvgIcon-root': {
            color: ColourPalette.palette.primary['40'],
          },
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { color: 'default' },
          style: {
            color: ColourPalette.palette.neutral['30'],
            background: ColourPalette.palette.neutral['85'],
          },
        },
        {
          props: { variant: 'bright' },
          style: {
            color: ColourPalette.palette.neutral['30'],
            background: ColourPalette.palette.neutral['85'],
          },
        },
        {
          props: { variant: 'medium' },
          style: {
            color: ColourPalette.palette.neutral['10'],
            background: ColourPalette.palette.neutral['85'],
          },
        },
        {
          props: { variant: 'dark' },
          style: {
            color: '#fff',
            background: ColourPalette.palette.neutral['30'],
          },
        },
        {
          props: { color: 'info' },
          style: {
            color: ColourPalette.palette.primary['20'],
            background: ColourPalette.palette.primary['99'],
          },
        },
        {
          props: { color: 'info', variant: 'medium' },
          style: {
            color: ColourPalette.palette.primary['20'],
            background: ColourPalette.palette.primary['90'],
          },
        },
        {
          props: { color: 'info', variant: 'dark' },
          style: {
            color: '#fff',
            background: ColourPalette.palette.primary['20'],
          },
        },
        {
          props: { color: 'warning' },
          style: {
            color: ColourPalette.palette.warning['20'],
            background: ColourPalette.palette.warning['95'],
          },
        },
        {
          props: { color: 'warning', variant: 'medium' },
          style: {
            color: ColourPalette.palette.warning['20'],
            background: ColourPalette.palette.warning['70'],
          },
        },
        {
          props: { color: 'warning', variant: 'dark' },
          style: {
            color: '#fff',
            background: ColourPalette.palette.warning['30'],
          },
        },
        {
          props: { color: 'success' },
          style: {
            color: ColourPalette.palette.success['20'],
            background: ColourPalette.palette.success['100'],
          },
        },
        {
          props: { color: 'success', variant: 'medium' },
          style: {
            color: ColourPalette.palette.success['20'],
            background: ColourPalette.palette.success['90'],
          },
        },
        {
          props: { color: 'success', variant: 'dark' },
          style: {
            color: '#fff',
            background: ColourPalette.palette.success['30'],
          },
        },
        {
          props: { color: 'error' },
          style: {
            color: ColourPalette.palette.danger['20'],
            background: ColourPalette.palette.danger['95'],
          },
        },
        {
          props: { color: 'error', variant: 'medium' },
          style: {
            color: ColourPalette.palette.danger['20'],
            background: ColourPalette.palette.danger['80'],
          },
        },
        {
          props: { color: 'error', variant: 'dark' },
          style: {
            color: '#fff',
            background: ColourPalette.palette.danger['30'],
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: '2px',
          // ChipSelect
          '&.MuiAutocomplete-tag': {
            borderRadius: '16px',
            '& .MuiChip-label': {
              fontSize: typography.body4?.fontSize,
              fontWeight: typography.body4?.fontWeight,
              lineHeight: typography.body4?.lineHeight,
            },
          },
        },
        label: {
          padding: '0 6px',
          fontSize: typography.body3Medium?.fontSize,
          fontWeight: typography.body3Medium?.fontWeight,
          lineHeight: typography.body3Medium?.lineHeight,
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          '& .MuiPickersDay-root.MuiPickersDay-today, .MuiPickersDay-root[aria-current]':
            {
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: ColourPalette.palette.primary['90'],
            },
          '& .MuiPickersDay-root:not(.Mui-selected)': {
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: '#fff',
          },
          '& .MuiPickersDay-root.Mui-selected, .MuiPickersYear-yearButton.Mui-selected':
            {
              color: '#fff',
              backgroundColor: ColourPalette.palette.primary['40'],
              borderColor: ColourPalette.palette.primary['40'],
              '&:hover': {
                backgroundColor: ColourPalette.palette.primary['50'],
                borderColor: ColourPalette.palette.primary['50'],
              },
              '&:focus': {
                color: '#fff',
                backgroundColor: ColourPalette.palette.primary['40'],
                borderColor: ColourPalette.palette.primary['40'],
                outlineColor: ColourPalette.palette.primary['90'],
                outlineWidth: '2px',
                outlineStyle: 'solid',
                outlineOffset: '2px',
              },
            },
          // Calendar
          '& .MuiDayCalendar-weekDayLabel': {
            color: ColourPalette.palette.neutral['40'],
          },
          '& .MuiPickersDay-root': {
            fontSize: typography.body3Medium?.fontSize,
            fontWeight: typography.body3Medium?.fontWeight,
            lineHeight: typography.body3Medium?.lineHeight,
            color: ColourPalette.palette.neutral['10'],
            '&:hover': {
              backgroundColor: ColourPalette.palette.neutral['90'],
            },
            '&.Mui-disabled': {
              color: ColourPalette.palette.neutral['55'],
            },
          },
          // Change year
          '& .MuiPickersCalendarHeader-label': {
            fontSize: typography.body2Bold?.fontSize,
            fontWeight: typography.body2Bold?.fontWeight,
            lineHeight: typography.body2Bold?.lineHeight,
            color: ColourPalette.palette.neutral['10'],
          },
          '& .MuiPickersArrowSwitcher-root': {},
          '& .MuiPickersCalendarHeader-switchViewIcon': {
            color: ColourPalette.palette.primary['40'],
          },
          '& .MuiPickersYear-yearButton': {
            fontSize: typography.body3Medium?.fontSize,
            fontWeight: typography.body3Medium?.fontWeight,
            lineHeight: typography.body3Medium?.lineHeight,
            '&:hover': {
              backgroundColor: ColourPalette.palette.neutral['90'],
            },
          },
          // Change month arrows
          '& .MuiPickersArrowSwitcher-button': {
            margin: 0,
            color: ColourPalette.palette.primary['40'],
          },
          '& .MuiPickersArrowSwitcher-spacer': {
            width: '8px',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          ...typography,
          fontSize: 16,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: '-0.2px',
          borderRadius: 4,
          '& fieldset': {
            borderColor: ColourPalette.palette.neutral['70'],
          },
          '&.MuiOutlinedInput-root': {
            fontWeight: 600,
            color: ColourPalette.palette.neutral['10'],
            '& legend': {
              fontSize: '12px', // this font-size is set only for scaled floating label
            },
          },
          '& .MuiSvgIcon-root': {
            color: ColourPalette.palette.primary['40'],
          },
          '&:hover': {
            '& fieldset, fieldset.MuiOutlinedInput-notchedOutline:not[.Mui-error]':
              {
                borderColor: ColourPalette.palette.neutral['60'],
              },
          },
          '&:focus-visible, &.Mui-focused:not[.Mui-error]': {
            '& fieldset, & fieldset.MuiOutlinedInput-notchedOutline': {
              borderColor: ColourPalette.palette.primary['40'],
            },
          },
          '&.Mui-error': {
            backgroundColor: ColourPalette.palette.danger['100'],
            '.MuiInputBase-input': {
              backgroundColor: ColourPalette.palette.danger['100'],
            },
            'fieldset, & fieldset.MuiOutlinedInput-notchedOutline': {
              borderColor: ColourPalette.palette.danger['30'],
            },
          },
          '&:readonly': {
            color: ColourPalette.palette.neutral['40'],
          },
          '&.Mui-readOnly.MuiInputBase-readOnly': {
            color: ColourPalette.palette.neutral['40'],
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          paddingTop: '8px',
          paddingBottom: '16px',
          fontSize: 14,
          '&.MuiTextField-root': {
            '& .MuiInputLabel-root': {
              [breakpoints.down('sm')]: {
                margin: 0,
              },
            },
          },
          '& .MuiInputBase-input': {
            paddingTop: '10px',
            paddingBottom: '10px',
            fontWeight: 600,
          },
          '& .MuiInputBase-inputMultiline': {
            '&::placeholder': {
              color: ColourPalette.palette.neutral['40'],
              fontWeight: 400,
              opacity: 1,
            },
          },
          '& input[type="password"]': {
            fontFamily: 'password',
            fontKerning: 'none',
            letterSpacing: '4px',
            [breakpoints.down('sm')]: {
              height: '20px',
              fontSize: typography?.body2?.fontSize,
            },
          },
          '& .MuiInputLabel-root': {
            top: 'unset',
            lineHeight: '20px',
            fontSize: 14,
            color: ColourPalette.palette.neutral['40'],
            [breakpoints.down('sm')]: {
              margin: 0,
            },
            '&.Mui-focused:not[".static-label"]': {
              lineHeight: '24px',
              fontSize: 16, // this font-size is set only for scaled floating label
              color: ColourPalette.palette.neutral['50'],
            },
            '&.MuiFormLabel-filled:not[".static-label"]': {
              lineHeight: '24px',
              fontSize: 16, // this font-size is set only for scaled floating label
            },
            '&.MuiInputLabel-shrink': {
              lineHeight: '24px',
              fontSize: 16, // this font-size is set only for scaled floating label
            },
          },
          '& .MuiFormHelperText-root': {
            color: ColourPalette.palette.neutral['40'],
            '&.Mui-error': {
              color: ColourPalette.palette.danger['30'],
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            color: ColourPalette.palette.danger['30'],
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          fontSize: typography.body4Bold?.fontSize,
          fontWeight: typography.body4Bold?.fontWeight,
          lineHeight: typography.body4Bold?.lineHeight,
          color: ColourPalette.palette.neutral['40'],
          backgroundColor: 'transparent',
          borderRadius: '4px',
          '&:hover': {
            color: ColourPalette.palette.primary.main,
            backgroundColor: ColourPalette.palette.neutral['95'],
          },
          '&:focus-visible': {
            backgroundColor: 'transparent',
          },
          '&:active': {
            color: ColourPalette.palette.primary.main,
            backgroundColor: ColourPalette.palette.neutral['85'],
          },
          '&.Mui-selected': {
            color: ColourPalette.palette.primary.main,
            backgroundColor: ColourPalette.palette.neutral['90'],
          },
          '&.Mui-disabled': {
            opacity: 1,
            color: ColourPalette.palette.neutral['55'],
            backgroundColor: 'transparent',
          },
          '&.MuiPaginationItem-previousNext:not(.Mui-disabled)': {
            color: ColourPalette.palette.primary.main,
          },
          '&.MuiPaginationItem-sizeLarge': {
            fontSize: typography.body3Bold?.fontSize,
            fontWeight: typography.body3Bold?.fontWeight,
            lineHeight: typography.body3Bold?.lineHeight,
          },
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          padding: '8px',
          '&:hover': {
            backgroundColor: 'unset',
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: KeyboardArrowDownIcon,
        MenuProps: {
          sx: {
            '& .MuiPaper-root': {
              marginTop: '4px',
              borderRadius: '8px',
              boxShadow: '4px 4px 20px 0px rgba(6, 37, 113, 0.16)',
            },
            '& .MuiMenu-list': {
              padding: '8px',
            },
            '& .MuiMenuItem-root': {
              padding: '8px',
              lineHeight: typography?.body3?.lineHeight,
              fontSize: typography?.body3?.fontSize,
              color: ColourPalette.palette.neutral['30'],
              borderRadius: '4px',
              '&:hover': {
                color: ColourPalette.palette.neutral['10'],
                backgroundColor: ColourPalette.palette.neutral['90'],
              },
              '&.Mui-focusVisible': {
                color: ColourPalette.palette.neutral['10'],
                backgroundColor: ColourPalette.palette.neutral['90'],
              },
            },
          },
        },
      },
      styleOverrides: {
        icon: {
          width: 24,
          height: 24,
          right: 12,
          color: ColourPalette.palette.primary['40'],
        },
        select: {
          paddingRight: 37,
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiAlert-root': {
            alignItems: 'center',
          },
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          height: 22,
          width: 40,
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 0,
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              '& + .MuiSwitch-track': {
                backgroundColor: ColourPalette.palette.primary['40'],
                opacity: 1,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                backgroundColor: ColourPalette.palette.neutral['85'],
              },
              '& .MuiSwitch-thumb': {
                borderColor: ColourPalette.palette.primary['40'],
                '&:before': {
                  display: 'block',
                  content: "''",
                  width: 24,
                  height: 24,
                  backgroundSize: 16,
                  backgroundPosition: '0 0',
                  backgroundRepeat: 'no-repeat',
                  backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
                    '#0041E9'
                  )}" d="M9 16.17 5.53 12.7a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41l4.18 4.18c.39.39 1.02.39 1.41 0L20.29 7.71c.39-.39.39-1.02 0-1.41a.9959.9959 0 0 0-1.41 0L9 16.17z"/></svg>')`,
                },
              },
            },
            '&.Mui-disabled': {
              '& + .MuiSwitch-track': {
                backgroundColor: ColourPalette.palette.neutral['85'],
                opacity: 1,
              },
              '& .MuiSwitch-thumb': {
                borderColor: ColourPalette.palette.neutral['85'],
              },
              '&.Mui-checked': {
                '& .MuiSwitch-thumb:before': {
                  backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
                    '#E9EBEE'
                  )}" d="M9 16.17 5.53 12.7a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41l4.18 4.18c.39.39 1.02.39 1.41 0L20.29 7.71c.39-.39.39-1.02 0-1.41a.9959.9959 0 0 0-1.41 0L9 16.17z"/></svg>')`,
                },
              },
            },
            '& .MuiSwitch-thumb': {
              color: '#fff',
              width: 24,
              height: 24,
              boxShadow: 'none',
              border: '2px solid',
              borderColor: ColourPalette.palette.neutral['70'],
            },
          },
          '& .MuiSwitch-track': {
            borderRadius: '12px',
            backgroundColor: ColourPalette.palette.neutral['70'],
            opacity: 1,
          },

          '&.MuiSwitch-sizeSmall': {
            height: 20,
            width: 32,
            '& .MuiSwitch-switchBase': {
              '&.Mui-checked': {
                transform: 'translateX(12px)',
                '& .MuiSwitch-thumb:before': {
                  width: 20,
                  height: 20,
                  backgroundSize: 14,
                  backgroundPosition: '1px 1px',
                },
              },
              '& .MuiSwitch-thumb': {
                width: 20,
                height: 20,
              },
            },
            '& .MuiSwitch-track': {
              borderRadius: '10px',
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          // Standard Tabs
          '.MuiTabs-flexContainer': {
            columnGap: '16px',
          },
          '& .MuiTabs-indicator': {
            backgroundColor: ColourPalette.palette.primary['40'],
          },
          // Box Style Tabs
          '&.boxStyle': {
            '.MuiTabs-flexContainer': {
              height: '100%',
              columnGap: 0,
            },
            '.MuiTab-root': {
              padding: '8px 32px',
              color: ColourPalette.palette.neutral['40'],
              backgroundColor: ColourPalette.palette.neutral['95'],
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: ColourPalette.palette.neutral['80'],
              borderBottom: '0 none',
              borderRadius: 0,
              '&:first-of-type': {
                borderTopLeftRadius: '8px',
              },
              '&:last-child': {
                borderTopRightRadius: '8px',
              },
              '&.Mui-selected': {
                color: ColourPalette.palette.primary['40'],
                backgroundColor: '#fff',
              },
              '&:focus': {
                outline: 0,
              },
              '&:focus-visible': {
                borderColor: ColourPalette.palette.neutral['80'],
              },
            },
          },
          // Tabs sizes
          '.MuiTab-root': {
            lineHeight: typography?.body4Medium?.lineHeight,
            fontSize: typography?.body4Medium?.fontSize,
            fontWeight: typography?.body4Medium?.fontWeight,
            textDecoration: 'none',
            textTransform: 'none',
          },
          '&.tabsSize-medium': {
            minHeight: '40px',
            '.MuiTab-root': {
              lineHeight: typography?.body3Medium?.lineHeight,
              fontSize: typography?.body3Medium?.fontSize,
              fontWeight: typography?.body3Medium?.fontWeight,
            },
          },
          '&.tabsSize-large': {
            '.MuiTab-root': {
              lineHeight: typography?.body2Medium?.lineHeight,
              fontSize: typography?.body2Medium?.fontSize,
              fontWeight: typography?.body2Medium?.fontWeight,
            },
          },
          a: {
            color: ColourPalette.palette.neutral['10'],
            '&:hover': {
              color: ColourPalette.palette.primary['40'],
              textDecoration: 'none',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          // Standard Tabs
          minHeight: 'auto',
          padding: '6px 9px',
          color: ColourPalette.palette.neutral['10'],
          backgroundColor: 'transparent',
          borderRadius: '4px',
          '&:hover': {
            textDecoration: 'none',
            color: ColourPalette.palette.primary['40'],
            background: ColourPalette.palette.neutral['90'],
          },
          '&.Mui-selected': {
            color: ColourPalette.palette.primary['40'],
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        arrow: {
          color: ColourPalette.palette.primary['20'],
        },
        popper: {
          maxWidth: 270,
        },
        tooltip: {
          display: 'block', // Allow smaller child line-height without altering parent line-height
          maxWidth: 270,
          padding: '4px 8px',
          lineHeight: typography?.body5?.lineHeight,
          fontSize: typography?.body5?.fontSize,
          fontWeight: typography?.body5?.fontWeight,
          backgroundColor: ColourPalette.palette.primary['20'],
        },
        root: {
          svg: {
            color: ColourPalette.palette.primary['40'],
          },
        },
      },
    },
  },
});

export default getEqMainThemeOptions;
